import {Button, Hotkey, IconButton, Link} from '@wandb/components';
import * as React from 'react';

import {urlPrefixed} from '../../../config';
import {type TileMediaSpec} from '../../MediaCard';
import {ZoomControl} from '../controls/ZoomControl';
import {useMediaBrowserModalContext} from './MediaBrowserModalContext';
import {RunSelectorDropdown} from './RunSelectorDropdown';

type MediaBrowserModalHeaderProps = {
  onClose: () => void;
  tileMediaSpec?: TileMediaSpec;
  fullScreenTitle?: React.ReactNode | null;
  titleLinkToRun?: string;
  labels?: string[];
  zoomLevel?: number;
  mediaKey?: string;
};

export const MediaBrowserModalHeader: React.FC<
  MediaBrowserModalHeaderProps
> = ({
  onClose,
  tileMediaSpec,
  fullScreenTitle,
  titleLinkToRun,
  labels,
  mediaKey,
}) => {
  const {
    navigation,
    selectRun,
    navigateToPrevious,
    navigateToNext,
    compareCurrentRunWithNextRun,
    zoom,
    setZoom,
    zoomToFitPercentage,
  } = useMediaBrowserModalContext();

  const {
    hasNext,
    hasPrevious,
    currentRun,
    availableRuns,
    canCompareCurrentRunWithNextRun,
  } = navigation;

  return (
    <div className="flex w-full py-16 px-16 border-b border-wb-border">
      <div className="flex flex-1 gap-8">
        <div className="flex flex-0 items-center gap-4 pr-8">
          <IconButton
            icon="close"
            variant="secondary"
            size="md"
            onClick={onClose}
            aria-label="Close modal"
            tooltip={{
              children: (
                <>
                  <span style={{margin: '2px'}}>Close modal</span>
                  <Hotkey keys={['Escape']} name="Close modal" m={2} />
                </>
              ),
            }}
          />
        </div>

        <div className="flex flex-1 items-center justify-center gap-4 text-[light-dark(var(--moon-600),var(--moon-400))]">
          <div className="text-lg/8 flex-0 h-full items-center justify-center font-semibold">
            {tileMediaSpec?.mediaKey || mediaKey || ''}
          </div>
          <span className="flex flex-0 h-full text-lg/8 items-center justify-center font-semibold">
            /
          </span>
          <div className="flex flex-1 h-full text-wb-text-primary text-lg/8 font-semibold items-center justify-start">
            {availableRuns && availableRuns.length > 1 && currentRun ? (
              <RunSelectorDropdown
                size="medium"
                selectedRun={currentRun}
                onRunSelect={selectRun}
                runsWithMedia={availableRuns}
                valueClasses="text-lg/8 p-0"
                menuItemClasses="text-sm/6"
              />
            ) : fullScreenTitle && labels?.includes('run') ? (
              <span className="text-lg/8 text-[light-dark(var(--moon-600),var(--moon-400))] font-semibold">
                <Link href={urlPrefixed(titleLinkToRun)} target="_blank">
                  {fullScreenTitle}
                </Link>
              </span>
            ) : (
              <span className="text-lg/8 text-[light-dark(var(--moon-600),var(--moon-400))] font-semibold">
                {fullScreenTitle}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-start mr-16">
        <ZoomControl
          source="modal"
          currentZoomLevel={zoom}
          setCurrentZoomLevel={setZoom}
          zoomToFitPercentage={zoomToFitPercentage}
        />
      </div>

      {canCompareCurrentRunWithNextRun && (
        <div className="flex items-center justify-start mr-16">
          <Button
            aria-label="fullscreen-modal-compare"
            data-test="fullscreen-modal-compare"
            startIcon="miller-columns"
            variant="secondary"
            size="md"
            onClick={compareCurrentRunWithNextRun}>
            Compare
          </Button>
        </div>
      )}

      {(hasNext || hasPrevious) && (
        <div className="flex flex-0 items-center justify-start">
          <div className="flex items-end justify-between gap-8">
            <Button
              aria-label="fullscreen-modal-prev-run"
              startIcon="chevron-back"
              variant="secondary"
              size="md"
              onClick={navigateToPrevious}
              isDisabled={!hasPrevious}
              tooltip={{
                children: (
                  <>
                    <span style={{margin: '2px'}}>Previous image in panel</span>
                    <Hotkey
                      keys={['ArrowLeft']}
                      name="Previous image in panel"
                      m={2}
                    />
                  </>
                ),
              }}>
              Previous
            </Button>
            <Button
              aria-label="fullscreen-modal-next-run"
              variant="secondary"
              size="md"
              onClick={navigateToNext}
              isDisabled={!hasNext}
              endIcon="chevron-next"
              tooltip={{
                children: (
                  <>
                    <span style={{margin: '2px'}}>Next image in panel</span>
                    <Hotkey
                      keys={['ArrowRight']}
                      name="Next image in panel"
                      m={2}
                    />
                  </>
                ),
              }}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
